import { isMobile } from '@zola-helpers/client/dist/es/util/responsive';
import { CheckboxFieldAdapter } from '@zola/zola-ui/src/components/Form/CheckboxField';
import FinalFormDropdown from '@zola/zola-ui/src/components/Form/dropdownV3/FinalFormDropdown';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

import cx from 'classnames';
import { Field } from 'react-final-form';

import { REQUIRED } from '~/components/common/form/commonValidations';
import * as Dates from '~/components/common/form/dates';
import { RankedDateField } from '~/components/common/form/WeddingDateField/types';

import { BookedDateError } from './BookedDateError';
import { DateError } from './DateError';

import styles from './weddingDateField.module.less';

export type WeddingDateFieldProps = {
  label?: string;

  /** Is this the wedding date for non-venues (undefined) or one of the Ranked dates for venue dates */
  field?: RankedDateField;

  removeField?: () => void;

  forceErrorsVisible?: boolean;
};

/**
 * Prompts for a wedding date in the inquiry flow.
 *
 * Wedding dates can be:
 * - Year / Month
 * - Year / Month / Date
 *
 * We have 2 different types of dates in the inquiry flow: wedding dates for non
 * venues which are a specific date (or year/month) and ranked date choices
 * for venues.  In ranked dates, you must a) provide the first ranked date field
 * b) may provide up to 3 ranked dates c) must provide year/month/day for each
 * ranked date.
 *
 * Validate on this component is very tricky.  We have 3 dropdowns for the
 * individual fields and we have form validation to check that wedding dates
 * are not already booked by the vendor and are in the future.
 *
 */
export const WeddingDateField = (props: WeddingDateFieldProps): JSX.Element => {
  const { label, field, removeField, forceErrorsVisible } = props;

  const showFlexibleCheckbox = field === undefined;

  const monthFieldName = field ? `${field}.weddingMonth` : 'weddingMonth';
  const dayFieldName: 'weddingDay' | `${RankedDateField}.weddingDay` = field
    ? `${field}.weddingDay`
    : 'weddingDay';
  const yearFieldName = field ? `${field}.weddingYear` : 'weddingYear';

  const currentYear = new Date().getFullYear();

  const removeButton = removeField ? (
    <LinkV2 role="button" type="button" onClick={removeField}>
      <i className="zola-ui-icon-close" role="img" aria-label="Remove Date" />
    </LinkV2>
  ) : null;

  return (
    <div className={styles.weddingDate} role="group" aria-label={label || 'Wedding Date'}>
      <label className={styles.weddingDateLabel}>
        {label} {isMobile() && removeButton}
      </label>
      <div className={styles.dateFields}>
        <FinalFormDropdown
          className={cx({ [styles.rankedDate]: Boolean(field) })}
          errorMessage={false}
          id={monthFieldName}
          name={monthFieldName}
          options={Dates.Months}
          placeholder="Month"
          tabIndex={0}
          validate={REQUIRED}
          validateFields={[monthFieldName, dayFieldName, yearFieldName]}
        />
        <FinalFormDropdown
          className={cx({ [styles.rankedDate]: Boolean(field) })}
          errorMessage={false}
          id={dayFieldName}
          name={dayFieldName}
          options={Dates.Dates}
          nullable
          placeholder="Day"
          validate={field ? REQUIRED : undefined}
          tabIndex={0}
        />
        <FinalFormDropdown
          className={cx({ [styles.rankedDate]: Boolean(field) })}
          errorMessage={false}
          id={yearFieldName}
          name={yearFieldName}
          options={Dates.getYears(4, currentYear)}
          placeholder="Year"
          tabIndex={0}
          validate={REQUIRED}
          validateFields={[monthFieldName, dayFieldName, yearFieldName]}
        />
        {!isMobile() && removeButton}
      </div>
      <DateError
        field={field}
        forceVisible={forceErrorsVisible}
        className={styles.weddingDateError}
      />

      {showFlexibleCheckbox && (
        <Field
          className={styles.flexibleOnDate}
          component={CheckboxFieldAdapter}
          id="flexibleOnDate"
          inline
          label="This date isn’t set yet and can change"
          name="flexibleOnDate"
          tabIndex={0}
          type="checkbox"
          validateFields={[]}
        />
      )}

      <BookedDateError
        field={field}
        forceVisible={forceErrorsVisible}
        className={styles.bookedDateError}
      />
    </div>
  );
};
